import type { BillingFeature } from '@kanos/domain';
import { api, failure } from '@kanos/web-core';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Lock } from 'lucide-react';
import type { ReactNode } from 'react';
import { useMe } from '../me';
import { keys } from './queries';

/** What the firm may use (ADR-022), for every member; the API checks every call anyway. */
const featuresQuery = (tenantId: string) =>
  queryOptions({
    queryKey: [...keys.all(tenantId), 'features'] as const,
    queryFn: async () => {
      const { data, error, response } = await api.GET('/api/v1/firm/features');
      if (!data) throw failure('getFirmFeatures', error, response.status);
      return data;
    },
  });

/** Whether the firm may use a feature; `false` while that is not known yet. */
export function useFeature(): (feature: BillingFeature) => boolean {
  const me = useMe();
  const features = useQuery(featuresQuery(me.activeTenantId));
  return (feature) =>
    features.data !== undefined &&
    (!features.data.configured || features.data.features.includes(feature));
}

const productOf: Record<BillingFeature, string> = {
  'check-point': 'gehört zu CHECK-POINT, dem Mandantenportal',
  'mission-point': 'gehört zu MISSION-POINT',
  'compliance-monitoring': 'gehört zum Compliance-Monitoring',
  integrations: 'gehört zu jedem Tarif: Die Schnittstellen sind überall enthalten',
};

/**
 * A part of the app that comes with a product (ADR-022): shown where the firm's plan includes
 * it, and while billing is off; otherwise a word on where it comes from - with the way there
 * for those who choose the plan. Comfort only: the API refuses the calls itself.
 */
export function FeatureGate({
  feature,
  children,
}: {
  feature: BillingFeature;
  children: ReactNode;
}) {
  const me = useMe();
  const features = useQuery(featuresQuery(me.activeTenantId));
  // Not known yet, or not to be read: the page as it is - the API refuses what the plan lacks,
  // and a page held back for the plan would load after it, one wait after another.
  if (!features.data) return children;
  const { configured, features: included } = features.data;
  return !configured || included.includes(feature) ? (
    children
  ) : (
    <div className="glass grid gap-3 rounded-lg p-6">
      <p className="flex items-center gap-2 font-semibold">
        <Lock aria-hidden className="size-4 shrink-0" />
        Nicht im Tarif der Kanzlei
      </p>
      <p className="text-muted">
        Dieser Bereich {productOf[feature]}. Die Basis mit Basisdaten und Pflichtenübersicht bleibt
        kostenlos; den ersten Tarif kann die Kanzlei kostenlos testen.
      </p>
      {me.firmAdmin ? (
        <Link
          to="/settings/$area"
          params={{ area: 'billing' }}
          className="inline-flex min-h-11 items-center justify-self-start font-semibold text-accent-text underline-offset-4 outline-none hover:underline focus-visible:outline-2 focus-visible:outline-focus"
        >
          Zur Abrechnung
        </Link>
      ) : (
        <p className="text-sm text-muted">
          Den Tarif wählen Admins der Kanzlei unter Einstellungen › Abrechnung & Recht › Abonnement.
        </p>
      )}
    </div>
  );
}
